import { computed, inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, withComputed, withMethods, withState } from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';
import { forkJoin, map } from 'rxjs';
import { EntitlementsDataService } from 'src/shared/services/entitlements.data.service';
import { accountexternal } from 'src/shared/services/client/client';
import { AvailablePackages } from 'src/shared/stores/entitlements/cruftsclub/models/cruftsclub';
import { sortEntitlements } from 'src/shared/stores/entitlements/cruftsclub/utils/cruftsclub.utils';

const CRUFT_PACKAGE_CODES = {
  starter: 'KC_STARTER_PHASE_TWO',
  plus: 'KC_PLUS_PHASE_TWO',
  ultimate: 'KC_UNLIMITED',
} as const;

export type CruftPackagesState = 'initial' | 'loading' | 'complete' | 'error';

export type CruftPackagesFeatureState = {
  cruftPackagesState: CruftPackagesState;
  cruftPackagesError: string;
  starterPackage: accountexternal.GetPackageByCodeResponse | null;
  plusPackage: accountexternal.GetPackageByCodeResponse | null;
  ultimatePackage: accountexternal.GetPackageByCodeResponse | null;
};

const initialState: CruftPackagesFeatureState = {
  cruftPackagesState: 'initial',
  cruftPackagesError: '',
  starterPackage: null,
  plusPackage: null,
  ultimatePackage: null,
};

export function withCruftPackages() {
  return signalStoreFeature(
    withState(initialState),
    withComputed((store) => ({
      cruftPackages: computed((): AvailablePackages | null => {
        const starter = store.starterPackage();
        const plus = store.plusPackage();
        const ultimate = store.ultimatePackage();
        if (starter && plus && ultimate) {
          return { starter, plus, ultimate };
        }
        return null;
      }),
    })),
    withMethods((store, entitlementsService = inject(EntitlementsDataService)) => {
      const getSortedPackageByCode = (code: string) =>
        entitlementsService.getPackageByCode(code).pipe(map(sortEntitlements));

      return {
        getPackageByCode(code: string) {
          return getSortedPackageByCode(code);
        },

        loadCruftPackages(): Promise<void> {
          patchState(store, {
            cruftPackagesState: 'loading' as CruftPackagesState,
            cruftPackagesError: '',
          });
          const request$ = forkJoin({
            starter: getSortedPackageByCode(CRUFT_PACKAGE_CODES.starter),
            plus: getSortedPackageByCode(CRUFT_PACKAGE_CODES.plus),
            ultimate: getSortedPackageByCode(CRUFT_PACKAGE_CODES.ultimate),
          }).pipe(
            tapResponse({
              next: (packages) =>
                patchState(store, {
                  starterPackage: packages.starter,
                  plusPackage: packages.plus,
                  ultimatePackage: packages.ultimate,
                  cruftPackagesState: 'complete' as CruftPackagesState,
                }),
              error: (error: unknown) =>
                patchState(store, {
                  cruftPackagesState: 'error' as CruftPackagesState,
                  cruftPackagesError: error instanceof Error ? error.message : String(error),
                }),
            })
          );
          return firstValueFrom(request$).then(() => undefined);
        },
      };
    })
  );
}
